import React, { useEffect, useState, useCallback } from "react";
import HeartLoader from "@/components/HeartLoader";
import { Link } from "react-router-dom";
import {
  Megaphone, ShieldCheck, Loader2, Send, Save, FlaskConical, Trash2, Plus,
  Users, MailOpen, MousePointerClick, UserX, LayoutTemplate, GitBranch, BarChart3,
  Clock, Check, X, ChevronDown, ChevronUp, PlayCircle,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const SEGMENTS = [
  { v: "all", l: "Everyone" },
  { v: "approved", l: "Approved members" },
  { v: "pending", l: "Awaiting approval" },
  { v: "waitlisted", l: "Waitlisted" },
];

const TABS = [
  { v: "overview", l: "Overview", icon: BarChart3 },
  { v: "campaigns", l: "Campaigns", icon: Megaphone },
  { v: "templates", l: "Templates", icon: LayoutTemplate },
  { v: "sequences", l: "Follow-ups", icon: GitBranch },
  { v: "audience", l: "Audience", icon: Users },
];

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-widest text-sec mb-1">{label}</div>
    {children}
  </label>
);

const Stat = ({ icon: Icon, label, value, tint = "text-brand", testid }) => (
  <div className="rounded-xl border border-rud bg-white/5 p-4" data-testid={testid}>
    <Icon size={16} className={tint} />
    <div className="text-2xl font-serif mt-2">{value}</div>
    <div className="text-xs text-sec">{label}</div>
  </div>
);

// ------------------------------ Overview ------------------------------
function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/marketing/overview").then(({ data }) => setO(data)).catch(() => {}); }, []);
  if (!o) return <div className="py-10 text-center text-sec"><HeartLoader /></div>;
  return (
    <div data-testid="mk-overview">
      {!o.mail_live && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 text-sm text-amber-200 mb-4">
          Email delivery is in <b>preview/mock</b> mode — set a Resend key to send for real.
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Stat icon={Users} label="Audience (opted-in)" value={o.audience} testid="ov-audience" />
        <Stat icon={Megaphone} label="Campaigns" value={o.campaigns} testid="ov-campaigns" />
        <Stat icon={Send} label="Emails sent" value={o.sent} testid="ov-sent" />
        <Stat icon={MailOpen} label="Open rate" value={`${o.open_rate}%`} tint="text-emerald-400" testid="ov-open" />
        <Stat icon={MousePointerClick} label="Click rate" value={`${o.click_rate}%`} tint="text-sky-400" testid="ov-click" />
        <Stat icon={UserX} label="Unsubscribed" value={o.unsubscribed} tint="text-red-300" testid="ov-unsub" />
      </div>
    </div>
  );
}

// ------------------------------ Audience ------------------------------
function Audience() {
  const [status, setStatus] = useState("all");
  const [country, setCountry] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/marketing/segments/preview?status=${status}&country=${country.trim()}`);
      setRes(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { preview(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-4" data-testid="mk-audience">
      <div className="rud-card rounded-2xl p-4 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <Field label="Segment">
          <select data-testid="aud-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm">
            {SEGMENTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </Field>
        <Field label="Country code (optional)">
          <input data-testid="aud-country" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} placeholder="e.g. IN" maxLength={2} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
        </Field>
        <button data-testid="aud-preview" onClick={preview} disabled={busy} className="btn-primary rounded-lg px-4 h-11 text-sm flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />} Preview
        </button>
      </div>
      {res && (
        <div className="rud-card rounded-2xl p-5" data-testid="aud-result">
          <div className="text-3xl font-serif text-brand">{res.count}</div>
          <div className="text-sm text-sec">recipients match this segment</div>
          {res.sample?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {res.sample.map((e) => <span key={e} className="text-[11px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-sec">{e}</span>)}
              {res.count > res.sample.length && <span className="text-[11px] text-sec">+{res.count - res.sample.length} more</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------ Templates ------------------------------
function Templates({ onPick }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", subject: "", html: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/marketing/templates").then(({ data }) => setItems(data.templates || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the template a name."); return; }
    setBusy(true);
    try {
      if (form.id) await api.put(`/marketing/templates/${form.id}`, form);
      else await api.post("/marketing/templates", form);
      toast.success("Template saved.");
      setForm({ name: "", subject: "", html: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/marketing/templates/${id}`); load(); } catch { /* noop */ } };
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="mk-templates">
      <div className="rud-card rounded-2xl p-4 space-y-3">
        <div className="text-sm font-medium">{form.id ? "Edit template" : "New template"}</div>
        <Field label="Name"><input data-testid="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></Field>
        <Field label="Subject"><input data-testid="tpl-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></Field>
        <Field label="HTML body"><textarea data-testid="tpl-html" rows={8} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm font-mono resize-y" placeholder="<h1>Hello!</h1>" /></Field>
        <div className="flex gap-2">
          <button data-testid="tpl-save" onClick={save} disabled={busy} className="btn-primary rounded-full h-9 px-5 text-sm flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
          {form.id && <button onClick={() => setForm({ name: "", subject: "", html: "" })} className="btn-ghost rounded-full h-9 px-4 text-sm">Cancel</button>}
        </div>
      </div>
      <div className="space-y-2">
        {items.length === 0 ? <p className="text-sec text-sm py-6 text-center">No templates yet.</p> : items.map((t) => (
          <div key={t.id} data-testid={`tpl-row-${t.id}`} className="rud-card rounded-xl p-3 flex items-center gap-3">
            <LayoutTemplate size={16} className="text-brand shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{t.name}</div>
              <div className="text-[11px] text-sec truncate">{t.subject || "No subject"}</div>
            </div>
            {onPick && <button data-testid={`tpl-use-${t.id}`} onClick={() => onPick(t)} className="btn-ghost rounded-lg px-3 py-1.5 text-xs">Use</button>}
            <button onClick={() => setForm(t)} className="btn-ghost rounded-lg px-3 py-1.5 text-xs">Edit</button>
            <button data-testid={`tpl-del-${t.id}`} onClick={() => del(t.id)} className="text-sec hover:text-red-400"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------ Campaigns ------------------------------
function Campaigns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", subject: "", html: "", status: "all", country: "", schedule_at: "" });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/marketing/campaigns"); setItems(data.campaigns || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const buildBody = (send_now) => ({
    name: form.name.trim(), subject: form.subject.trim(), html: form.html,
    segment: { status: form.status, country: form.country.trim() },
    schedule_at: form.schedule_at ? new Date(form.schedule_at).toISOString() : null,
    send_now,
  });

  const create = async (send_now) => {
    if (!form.name.trim() || !form.subject.trim()) { toast.error("Name and subject are required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/campaigns", buildBody(send_now));
      toast.success(send_now ? "Campaign is sending 🚀" : data.status === "scheduled" ? "Campaign scheduled ⏰" : "Draft saved.");
      setShowNew(false); setForm({ name: "", subject: "", html: "", status: "all", country: "", schedule_at: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a test email address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/test-send", { to: testTo.trim(), subject: form.subject || "Test", html: form.html || "<p>Test</p>" });
      toast.success(data.ok ? `Test sent to ${testTo}` : data.mocked ? "Mock mode — logged only" : `Failed: ${data.error || "unknown"}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendNow = async (id) => { try { await api.post(`/marketing/campaigns/${id}/send`); toast.success("Sending 🚀"); setTimeout(load, 1500); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async (id) => { try { await api.delete(`/marketing/campaigns/${id}`); load(); } catch { /* noop */ } };

  const badge = (s) => ({
    draft: "bg-white/10 text-sec", scheduled: "bg-amber-400/15 text-amber-300",
    sending: "bg-sky-400/15 text-sky-300", sent: "bg-emerald-400/15 text-emerald-300",
  }[s] || "bg-white/10 text-sec");

  return (
    <div className="space-y-4" data-testid="mk-campaigns">
      <button data-testid="new-campaign-btn" onClick={() => setShowNew((v) => !v)} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2">
        {showNew ? <X size={15} /> : <Plus size={15} />} {showNew ? "Close" : "New campaign"}
      </button>

      {showNew && (
        <div className="rud-card rounded-2xl p-4 space-y-3" data-testid="new-campaign-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Campaign name"><input data-testid="cmp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></Field>
            <Field label="Audience segment">
              <select data-testid="cmp-segment" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm">
                {SEGMENTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Country filter (optional)"><input data-testid="cmp-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} maxLength={2} placeholder="IN" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></Field>
            <Field label="Schedule (optional)"><input data-testid="cmp-schedule" type="datetime-local" value={form.schedule_at} onChange={(e) => setForm({ ...form, schedule_at: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></Field>
          </div>
          <div className="flex items-center justify-between">
            <Field label="Subject" ><span /></Field>
            <button onClick={() => setShowPicker((v) => !v)} className="text-xs text-brand flex items-center gap-1 mb-1">
              <LayoutTemplate size={13} /> Load from template {showPicker ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          </div>
          {showPicker && <div className="rounded-xl border border-rud bg-black/20 p-3"><Templates onPick={(t) => { setForm((f) => ({ ...f, subject: t.subject || f.subject, html: t.html || f.html })); setShowPicker(false); toast.success("Template loaded."); }} /></div>}
          <input data-testid="cmp-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject line" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <Field label="HTML body"><textarea data-testid="cmp-html" rows={9} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm font-mono resize-y" placeholder="<h1>Hello from My Kind</h1>" /></Field>
          {form.html && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-sec mb-1">Live preview</div>
              <iframe title="preview" data-testid="cmp-preview" srcDoc={form.html} className="w-full h-56 rounded-lg border border-rud bg-white" />
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center pt-1">
            <button data-testid="cmp-send-now" onClick={() => create(true)} disabled={busy} className="btn-primary rounded-full h-9 px-5 text-sm flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {form.schedule_at ? "Schedule" : "Send now"}</button>
            <button data-testid="cmp-save-draft" onClick={() => create(false)} disabled={busy} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-2"><Save size={14} /> Save draft</button>
            <div className="flex-1" />
            <input data-testid="cmp-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@email.com" className="rud-input rounded-lg px-3 py-2 text-sm w-44" />
            <button data-testid="cmp-test-send" onClick={sendTest} disabled={busy} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-2"><FlaskConical size={14} /> Test</button>
          </div>
        </div>
      )}

      {loading ? <div className="py-10 text-center text-sec"><HeartLoader /></div> :
        items.length === 0 ? <p className="text-sec text-sm py-8 text-center">No campaigns yet. Create your first one above.</p> : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.id} data-testid={`cmp-row-${c.id}`} className="rud-card rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{c.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${badge(c.status)}`}>{c.status}</span>
                  </div>
                  <div className="text-[11px] text-sec truncate">{c.subject}</div>
                </div>
                {(c.status === "draft" || c.status === "scheduled") && (
                  <button data-testid={`cmp-sendnow-${c.id}`} onClick={() => sendNow(c.id)} className="btn-primary rounded-lg px-3 py-1.5 text-xs flex items-center gap-1"><PlayCircle size={13} /> Send now</button>
                )}
                <button data-testid={`cmp-del-${c.id}`} onClick={() => del(c.id)} className="text-sec hover:text-red-400"><Trash2 size={15} /></button>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                <div><div className="text-sm font-serif">{c.stats?.sent ?? 0}</div><div className="text-[10px] text-sec">Sent</div></div>
                <div><div className="text-sm font-serif text-emerald-400">{c.stats?.open_rate ?? 0}%</div><div className="text-[10px] text-sec">Opened</div></div>
                <div><div className="text-sm font-serif text-sky-400">{c.stats?.click_rate ?? 0}%</div><div className="text-[10px] text-sec">Clicked</div></div>
                <div><div className="text-sm font-serif text-red-300">{c.stats?.unsub ?? 0}</div><div className="text-[10px] text-sec">Unsub</div></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------ Sequences ------------------------------
function Sequences() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", status: "all", country: "", steps: [{ delay_days: 0, subject: "", html: "" }] });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/marketing/sequences").then(({ data }) => setItems(data.sequences || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setStep = (i, k, v) => setForm((f) => ({ ...f, steps: f.steps.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { delay_days: 3, subject: "", html: "" }] }));
  const rmStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim() || form.steps.some((s) => !s.subject.trim())) { toast.error("Name + every step needs a subject."); return; }
    setBusy(true);
    try {
      await api.post("/marketing/sequences", {
        name: form.name.trim(), segment: { status: form.status, country: form.country.trim() },
        steps: form.steps.map((s) => ({ delay_days: Number(s.delay_days) || 0, subject: s.subject, html: s.html })), active: true,
      });
      toast.success("Follow-up sequence created.");
      setShowNew(false); setForm({ name: "", status: "all", country: "", steps: [{ delay_days: 0, subject: "", html: "" }] }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const enroll = async (id) => { try { const { data } = await api.post(`/marketing/sequences/${id}/enroll`); toast.success(`Enrolled ${data.enrolled} member(s).`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggle = async (s) => { try { await api.put(`/marketing/sequences/${s.id}`, { name: s.name, segment: s.segment, steps: s.steps, active: !s.active }); load(); } catch { /* noop */ } };
  const del = async (id) => { try { await api.delete(`/marketing/sequences/${id}`); load(); } catch { /* noop */ } };

  return (
    <div className="space-y-4" data-testid="mk-sequences">
      <p className="text-sm text-sec">Automated drip emails. Enroll a segment and each member receives the steps in order, spaced by the delay you set.</p>
      <button data-testid="new-sequence-btn" onClick={() => setShowNew((v) => !v)} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2">{showNew ? <X size={15} /> : <Plus size={15} />} {showNew ? "Close" : "New sequence"}</button>

      {showNew && (
        <div className="rud-card rounded-2xl p-4 space-y-3" data-testid="new-sequence-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Sequence name"><input data-testid="seq-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></Field>
            <Field label="Segment"><select data-testid="seq-segment" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm">{SEGMENTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select></Field>
            <Field label="Country (optional)"><input data-testid="seq-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} maxLength={2} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></Field>
          </div>
          <div className="space-y-3">
            {form.steps.map((s, i) => (
              <div key={i} className="rounded-xl border border-rud bg-black/20 p-3 space-y-2" data-testid={`seq-step-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-brand">Step {i + 1}</div>
                  {form.steps.length > 1 && <button onClick={() => rmStep(i)} className="text-sec hover:text-red-400"><Trash2 size={14} /></button>}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock size={14} className="text-sec" /> Send after
                  <input data-testid={`seq-delay-${i}`} type="number" min={0} step="0.5" value={s.delay_days} onChange={(e) => setStep(i, "delay_days", e.target.value)} className="rud-input rounded-lg px-2 py-1 text-sm w-20" /> days
                </div>
                <input data-testid={`seq-subject-${i}`} value={s.subject} onChange={(e) => setStep(i, "subject", e.target.value)} placeholder="Subject" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
                <textarea data-testid={`seq-html-${i}`} rows={4} value={s.html} onChange={(e) => setStep(i, "html", e.target.value)} placeholder="<p>Body…</p>" className="rud-input w-full rounded-lg px-3 py-2 text-sm font-mono resize-y" />
              </div>
            ))}
            <button data-testid="seq-add-step" onClick={addStep} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-2"><Plus size={14} /> Add step</button>
          </div>
          <button data-testid="seq-save" onClick={save} disabled={busy} className="btn-primary rounded-full h-9 px-5 text-sm flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create sequence</button>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 ? <p className="text-sec text-sm py-6 text-center">No sequences yet.</p> : items.map((s) => (
          <div key={s.id} data-testid={`seq-row-${s.id}`} className="rud-card rounded-xl p-4 flex items-center gap-3">
            <GitBranch size={16} className="text-brand shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><span className="text-sm font-medium truncate">{s.name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.active ? "bg-emerald-400/15 text-emerald-300" : "bg-white/10 text-sec"}`}>{s.active ? "Active" : "Paused"}</span>
              </div>
              <div className="text-[11px] text-sec">{s.steps?.length || 0} steps · {s.enrolled || 0} enrolled</div>
            </div>
            <button data-testid={`seq-enroll-${s.id}`} onClick={() => enroll(s.id)} className="btn-primary rounded-lg px-3 py-1.5 text-xs flex items-center gap-1"><Users size={13} /> Enroll segment</button>
            <button data-testid={`seq-toggle-${s.id}`} onClick={() => toggle(s)} className="btn-ghost rounded-lg px-3 py-1.5 text-xs">{s.active ? "Pause" : "Resume"}</button>
            <button data-testid={`seq-del-${s.id}`} onClick={() => del(s.id)} className="text-sec hover:text-red-400"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------ Page ------------------------------
export default function MarketingConsole() {
  const { user } = useApp();
  const [tab, setTab] = useState("overview");

  if (user === undefined) return <div className="min-h-screen grid place-items-center text-sec"><HeartLoader /></div>;
  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="text-center" data-testid="marketing-denied">
          <ShieldCheck size={40} className="mx-auto text-brand mb-3" />
          <h1 className="font-serif text-2xl">Admins only</h1>
          <p className="text-sec text-sm mt-2">The marketing console is restricted. <Link to="/" className="text-brand hover:underline">Go home</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="marketing-console-page">
      <div className="flex items-center gap-2 text-brand mb-1"><Megaphone size={18} /> <span className="text-xs uppercase tracking-widest">Marketing</span></div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-serif text-3xl tracking-tight">Email Console</h1>
        <Link to="/admin/access" data-testid="back-to-admin" className="text-sm text-sec hover:text-brand flex items-center gap-1"><ShieldCheck size={14} /> Member approvals</Link>
      </div>
      <p className="text-sec text-sm mt-1">Reach your members with campaigns, saved templates and automated follow-ups — with open &amp; click tracking.</p>

      <div className="flex items-center gap-1.5 mt-6 mb-6 flex-wrap" data-testid="marketing-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.v} data-testid={`mk-tab-${t.v}`} onClick={() => setTab(t.v)}
              className={`h-9 px-3.5 rounded-full text-sm flex items-center gap-1.5 transition-colors ${tab === t.v ? "bg-brand text-black font-medium" : "bg-white/5 text-sec border border-white/10 hover:text-[var(--rud-text)]"}`}>
              <Icon size={14} /> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "campaigns" && <Campaigns />}
      {tab === "templates" && <Templates />}
      {tab === "sequences" && <Sequences />}
      {tab === "audience" && <Audience />}
    </div>
  );
}
